/**
 * Utilitaires pour la gestion centralisée des erreurs API
 * Fournit des messages d'erreur localisés et cohérents
 */

export interface ApiError extends Error {
  status?: number;
  code?: string;
  details?: Record<string, unknown>;
}

/**
 * Messages d'erreur localisés par code de statut HTTP
 */
const ERROR_MESSAGES: Record<number, string> = {
  400: 'Requête invalide : Vérifiez les données saisies',
  401: 'Session expirée : Veuillez vous reconnecter',
  403: 'Accès refusé : Seuls les administrateurs peuvent effectuer cette action',
  404: 'Ressource non trouvée',
  409: 'Conflit : Cette ressource existe déjà ou est en cours d\'utilisation',
  422: 'Données invalides : Vérifiez les champs requis',
  429: 'Trop de requêtes : Veuillez patienter avant de réessayer',
  500: 'Erreur serveur : Veuillez réessayer plus tard',
  502: 'Service temporairement indisponible',
  503: 'Service en maintenance : Veuillez réessayer plus tard',
};

/**
 * Codes d'erreur spécifiques pour les actions
 */
const ACTION_ERROR_CODES: Record<string, string> = {
  'CREATE_FORBIDDEN': 'Vous n\'avez pas les droits pour créer cet élément',
  'UPDATE_FORBIDDEN': 'Vous n\'avez pas les droits pour modifier cet élément',
  'DELETE_FORBIDDEN': 'Vous n\'avez pas les droits pour supprimer cet élément',
  'READ_FORBIDDEN': 'Vous n\'avez pas les droits pour consulter cet élément',
  'NETWORK_ERROR': 'Erreur de connexion : Vérifiez votre connexion internet',
  'TIMEOUT': 'Délai d\'attente dépassé : Veuillez réessayer',
  'VALIDATION_ERROR': 'Erreur de validation : Vérifiez les données saisies',
};

/**
 * Extrait le code de statut HTTP d'un message d'erreur
 */
const extractStatusFromMessage = (message: string): number | null => {
  const statusMatch = message.match(/Erreur (\d+):/);
  return statusMatch ? parseInt(statusMatch[1]) : null;
};

/**
 * Détermine le type d'action à partir du contexte
 */
const getActionFromContext = (context?: string): string => {
  if (!context) return 'UNKNOWN';
  
  const lowerContext = context.toLowerCase();
  if (lowerContext.includes('créer') || lowerContext.includes('create')) return 'CREATE';
  if (lowerContext.includes('modifier') || lowerContext.includes('update')) return 'UPDATE';
  if (lowerContext.includes('supprimer') || lowerContext.includes('delete')) return 'DELETE';
  if (lowerContext.includes('récupérer') || lowerContext.includes('fetch')) return 'READ';
  
  return 'UNKNOWN';
};

/**
 * Transforme une erreur brute en message d'erreur localisé
 */
export const getLocalizedErrorMessage = (
  error: unknown, 
  context?: string,
  fallbackMessage?: string
): string => {
  // Cas d'erreur null/undefined
  if (!error) {
    return fallbackMessage || 'Une erreur inattendue s\'est produite';
  }

  // Cas d'erreur de type string
  if (typeof error === 'string') {
    const status = extractStatusFromMessage(error);
    if (status && ERROR_MESSAGES[status]) {
      return ERROR_MESSAGES[status];
    }
    return error;
  }

  // Cas d'erreur de type Error
  if (error instanceof Error) {
    const apiError = error as ApiError;
    
    // Vérifier le statut HTTP
    if (apiError.status && ERROR_MESSAGES[apiError.status]) {
      return ERROR_MESSAGES[apiError.status];
    }

    // Extraire le statut du message
    const status = extractStatusFromMessage(apiError.message);
    if (status && ERROR_MESSAGES[status]) {
      return ERROR_MESSAGES[status];
    }

    // Vérifier les codes d'erreur spécifiques
    if (apiError.code && ACTION_ERROR_CODES[apiError.code]) {
      return ACTION_ERROR_CODES[apiError.code];
    }

    // Gestion spéciale pour les erreurs 403 selon le contexte
    if (status === 403 && context) {
      const action = getActionFromContext(context);
      const actionKey = `${action}_FORBIDDEN`;
      if (ACTION_ERROR_CODES[actionKey]) {
        return ACTION_ERROR_CODES[actionKey];
      }
    }

    // Retourner le message original si pas de correspondance
    return apiError.message;
  }

  // Cas d'objet avec propriété message
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return getLocalizedErrorMessage((error as { message: unknown }).message, context, fallbackMessage);
  }

  // Fallback final
  return fallbackMessage || 'Une erreur inattendue s\'est produite';
};

/**
 * Crée une erreur API typée avec message localisé
 */
export const createApiError = (
  status: number,
  message?: string,
  code?: string,
  details?: Record<string, unknown>
): ApiError => {
  const localizedMessage = message || ERROR_MESSAGES[status] || `Erreur HTTP ${status}`;
  
  const error = new Error(localizedMessage) as ApiError;
  error.status = status;
  error.code = code;
  error.details = details;
  
  return error;
};

/**
 * Vérifie si une erreur est liée aux permissions (401/403)
 */
export const isPermissionError = (error: unknown): boolean => {
  if (error instanceof Error) {
    const apiError = error as ApiError;
    return apiError.status === 401 || apiError.status === 403;
  }
  
  if (typeof error === 'string') {
    const status = extractStatusFromMessage(error);
    return status === 401 || status === 403;
  }
  
  return false;
};

/**
 * Vérifie si une erreur est une erreur de validation (422)
 */
export const isValidationError = (error: unknown): boolean => {
  if (error instanceof Error) {
    const apiError = error as ApiError;
    return apiError.status === 422;
  }
  
  if (typeof error === 'string') {
    const status = extractStatusFromMessage(error);
    return status === 422;
  }
  
  return false;
};

/**
 * Vérifie si une erreur est une erreur serveur (5xx)
 */
export const isServerError = (error: unknown): boolean => {
  if (error instanceof Error) {
    const apiError = error as ApiError;
    return (apiError.status || 0) >= 500;
  }
  
  if (typeof error === 'string') {
    const status = extractStatusFromMessage(error);
    return (status || 0) >= 500;
  }
  
  return false;
};

/**
 * Gestionnaire d'erreur pour les composants Vue
 * Retourne un objet avec le message et le type d'erreur
 */
export const handleApiError = (
  error: unknown,
  context?: string
): { message: string; type: 'permission' | 'validation' | 'server' | 'client' | 'unknown' } => {
  const message = getLocalizedErrorMessage(error, context);
  
  let type: 'permission' | 'validation' | 'server' | 'client' | 'unknown' = 'unknown';
  
  if (isPermissionError(error)) {
    type = 'permission';
  } else if (isValidationError(error)) {
    type = 'validation';
  } else if (isServerError(error)) {
    type = 'server';
  } else if (error instanceof Error) {
    type = 'client';
  }
  
  return { message, type };
}; 